import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Download, Inbox, LogOut, Mail, Phone, Users } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SelectField } from "@/components/SelectField";

const statusStyles = {
  new: "bg-gold/15 text-gold border-gold/40",
  contacted: "bg-sky-400/10 text-sky-300 border-sky-400/30",
  confirmed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  closed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
};

const serviceLabels = {
  domestic: "Domestic Tour",
  international: "International Tour",
  visa: "Visa Service",
};

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const token = localStorage.getItem("arihant_admin_token");
  const role = localStorage.getItem("arihant_admin_role") || "admin";

  const fetchEnquiries = useCallback(async () => {
    try {
      const res = await api.get("/admin/enquiries", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("arihant_admin_token");
        localStorage.removeItem("arihant_admin_role");
        navigate("/admin/login");
      } else if (err.response?.status === 403) {
        toast.error("Admin role required for this action.");
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchEnquiries();
  }, [token, navigate, fetchEnquiries]);

  const exportLeads = async () => {
    try {
      const res = await api.get("/admin/enquiries/export", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "arihant-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads CSV downloaded — ready for your CRM.");
    } catch {
      toast.error("Could not export leads.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/enquiries/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      if (res.data?.seat_reserved) {
        toast.success(`Confirmed — 1 seat reserved on the departure (${res.data.seats_left} left).`);
      } else {
        toast.success(`Marked as ${status}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status.");
    }
  };

  const logout = () => {
    localStorage.removeItem("arihant_admin_token");
    localStorage.removeItem("arihant_admin_role");
    navigate("/admin/login");
  };

  const counts = {
    all: enquiries.length,
    new: enquiries.filter((e) => e.status === "new").length,
    contacted: enquiries.filter((e) => e.status === "contacted").length,
    confirmed: enquiries.filter((e) => e.status === "confirmed").length,
    closed: enquiries.filter((e) => e.status === "closed").length,
  };

  const filtered = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-surface/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-logo">
            <BrandMark size={36} iconSize={16} />
            <span className="font-display text-xl text-cream">
              <BrandName />
              <span className="text-xs font-mono tracking-widest text-faded ml-3 uppercase">{role}</span>
            </span>
          </Link>
          <nav className="flex items-center gap-3 flex-wrap">
            <span data-testid="admin-tab-enquiries" className="text-xs text-ink bg-gold px-5 py-2.5 rounded-full font-semibold">
              Enquiries
            </span>
            <Link to="/admin/packages" data-testid="admin-tab-packages" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              Packages
            </Link>
            {role === "admin" && (
              <>
                <Link to="/admin/users" data-testid="admin-tab-users" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                  Users
                </Link>
                <Link to="/admin/settings" data-testid="admin-tab-settings" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                  Site Settings
                </Link>
              </>
            )}
            <button
              onClick={exportLeads}
              data-testid="export-leads-btn"
              className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors"
              title="Download all enquiries as CSV for your CRM"
            >
              <Download size={14} /> Export Leads
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-3">Enquiry Inbox</p>
          <h1 className="font-display text-3xl sm:text-4xl text-cream">Customer enquiries</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[
            { key: "all", label: "Total" },
            { key: "new", label: "New" },
            { key: "contacted", label: "Contacted" },
            { key: "confirmed", label: "Confirmed" },
            { key: "closed", label: "Closed" },
          ].map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`admin-filter-${c.key}`}
              className={`rounded-2xl border p-5 text-left transition-colors duration-200 ${
                filter === c.key ? "border-gold bg-gold/10" : "border-cream/10 bg-card/50 hover:border-gold/40"
              }`}
            >
              <p className="font-display text-3xl text-gold" data-testid={`admin-count-${c.key}`}>{counts[c.key]}</p>
              <p className="text-xs tracking-[0.2em] uppercase text-faded mt-1">{c.label}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4" data-testid="admin-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 rounded-2xl bg-card/60 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-cream/15 rounded-3xl" data-testid="admin-empty-state">
            <Inbox size={40} className="mx-auto text-faded mb-4" strokeWidth={1.5} />
            <p className="text-mist text-sm">No enquiries here yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((e, i) => (
              <motion.div
                key={e.id}
                data-testid={`admin-enquiry-card-${e.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-cream/10 bg-card/60 p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-5 justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-display text-xl text-cream">{e.full_name}</h3>
                      <span className={`text-[11px] font-mono tracking-widest uppercase px-3 py-1 rounded-full border ${statusStyles[e.status] || statusStyles.new}`}>
                        {e.status}
                      </span>
                      <span className="text-[11px] font-mono text-faded">{e.reference}</span>
                    </div>
                    <p className="text-sm text-gold mb-3">{serviceLabels[e.service_type] || e.service_type}</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-mist">
                      <span className="flex items-center gap-1.5"><Phone size={12} className="text-gold" /> {e.phone}</span>
                      <span className="flex items-center gap-1.5"><Mail size={12} className="text-gold" /> {e.email}</span>
                      {e.destination && <span className="flex items-center gap-1.5">Destination: {e.destination}</span>}
                      {e.travel_date && <span className="flex items-center gap-1.5"><Calendar size={12} className="text-gold" /> {e.travel_date}</span>}
                      <span className="flex items-center gap-1.5"><Users size={12} className="text-gold" /> {e.travelers} traveller(s)</span>
                    </div>
                    {e.message && <p className="mt-3 text-sm text-faded italic">"{e.message}"</p>}
                    <p className="mt-3 text-[11px] font-mono text-faded">
                      {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-[11px] font-mono tracking-widest uppercase text-faded">Status</label>
                    <SelectField
                      value={e.status}
                      onChange={(v) => updateStatus(e.id, v)}
                      testid={`admin-status-select-${e.id}`}
                      options={[
                        { value: "new", label: "New" },
                        { value: "contacted", label: "Contacted" },
                        { value: "confirmed", label: "Confirmed" },
                        { value: "closed", label: "Closed" },
                      ]}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
